// @flow
import { deviceIsWeatherStation } from '../../util/device'
import isEqual from 'lodash.isequal'

import type { DateObject } from '../../util/time'
import type { LocationModel } from './types'
import type { SelectorProps } from '../../util/redux'
import type { State } from '../../store/store'
import type { Weather } from '../../util/weather'
import type { WeatherStation } from '../../util/location'

export const locationSelector = (
  state: State,
  props: SelectorProps
): ?LocationModel => {
  // if there is a locationId props, use that, otherwise look for a locationId param
  const {
    // $FlowFixMe
    locationId,
    match: { params: { locationId: paramId } = {} } = {}
  } = props
  const id = locationId || paramId

  if (!id) return null

  return state.location[id] && state.location[id].created
    ? state.location[id]
    : null
}

export const scheduleRunsSelector = (
  state: State,
  props: Object
): ?Array<Object> => {
  const location = locationSelector(state, props)
  return location && location.META.runs ? location.META.runs : null
}

export const scheduleSkipsSelector = (
  state: State,
  props: Object
): ?Array<Object> => {
  const location = locationSelector(state, props)
  return location && location.META.skips ? location.META.skips : null
}

export const timeZoneSelector = (state: State, props: Object): ?string => {
  const location: ?LocationModel = locationSelector(state, props)
  return location && location.timeZone ? location.timeZone : null
}

/**
 * Find weather station id for a particular location
 * @param {object} state - Redux state
 * @param {object} props - Component properties, expected to have props from router
 * @returns {[string]} - The weather station ID for the location
 */
export const weatherStationIdSelector = (
  state: State,
  props: Object
): ?string => {
  const loc = locationSelector(state, props)
  let weatherStationId: ?string = null

  if (loc && loc.devices) {
    const foundStation = loc.devices.find(deviceIsWeatherStation)
    if (foundStation) {
      weatherStationId = foundStation.id
    }
  }

  return weatherStationId
}

/**
 * Find weather station for a particualr location in devices
 * @param {object} state - Redux state
 * @param {object} props - Component properties, expected to have props from router
 * @returns {object|null} - The weather station for the location
 */
export const weatherStationSelector = (
  state: State,
  props: Object
): ?WeatherStation => {
  const loc = locationSelector(state, props)
  let weatherStationInLoc = null
  let weatherStationId: ?string = null
  let weatherStation = null

  if (loc && loc.devices) {
    const foundStation = loc.devices.find(deviceIsWeatherStation)
    if (foundStation) {
      weatherStationInLoc = state.device[foundStation.id]
    }
  }

  if (
    weatherStationInLoc &&
    weatherStationInLoc.META &&
    weatherStationInLoc.META.details &&
    weatherStationInLoc.META.details.id
  ) {
    // $FlowFixMe
    weatherStationId = weatherStationInLoc.META.details.id
  }
  if (
    weatherStationId &&
    state.device[weatherStationId] &&
    state.device[weatherStationId].META &&
    state.device[weatherStationId].META.details
  ) {
    weatherStation = state.device[weatherStationId].META.details
  }

  // $FlowFixMe
  return weatherStation
}

/**
 * Get single day's weather
 * @param  {object} state - Redux state
 * @param  {string} locationId - Location ID
 * @param  {object} date - Date object
 * @return {[object]} - Day's weather or null
 */
export const weatherSelector = (
  state: State,
  locationId: string,
  date: DateObject
): ?Weather => {
  let weather = null
  const location = locationSelector(state, {
    locationId,
    deviceId: '',
    history: {
      length: 0,
      push: () => {},
      goBack: () => {},
      replace: () => {}
    },
    location: {},
    match: {
      params: { deviceId: '', locationId: '', scheduleId: '', zoneId: '' }
    }
  })

  if (location && location.META && location.META.weatherForecast) {
    weather =
      location.META.weatherForecast.find(w => isEqual(w.date, date)) || null
  }

  return weather
}
